import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bankReconApi, type BankStatement, type BankStatementEntry, type LedgerType } from '../api/bankRecon';
import { partiesApi } from '../api/parties';
import { loanAccountsApi } from '../api/loanAccounts';
import type { Party, LoanAccount } from '@coreledger/shared-types';
import { FeatureGate } from '../components/FeatureGate';
import { PartyLedgerPicker } from '../components/PartyLedgerPicker';
import { LoanAccountSelector } from '../components/LoanAccountSelector';

const fmtN = (n: number) => '₹' + Math.abs(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

// Same 12 categories/labels ExpensesPage.tsx's DIRECT_CATS/INDIRECT_CATS use
// (and ledger-suggestion.ts's backend keyword dictionary suggests from) —
// duplicated here rather than shared, matching how this app already
// duplicates the expense-category list per app (see CLAUDE.md's Expense
// category note) rather than a single source of truth. Now a real, fixed
// dropdown rather than free text — confirming a category books a real
// Expense server-side (bank-recon.service.ts's bulkAssignLedger()), which
// requires resolving to one of these exact labels, not arbitrary text.
const EXPENSE_DIRECT_LABELS = ['Salary & Wages', 'Transport & Fuel', 'Repairs & Maintenance', 'Taxes & Duties'];
const EXPENSE_INDIRECT_LABELS = [
  'Rent / Lease', 'Utilities', 'Marketing & Advertising', 'Office Supplies',
  'Professional Fees', 'Insurance', 'Bank Charges', 'Miscellaneous',
];

// Mirrors ledger-suggestion.ts's OTHER_LABELS (backend validates against the
// same list, per side) — label-only categories with no real ledger/module
// behind them, same honest scope Expense itself started at. Never
// auto-suggested; only ever a user's own manual pick.
const OTHER_DEBIT_LABELS = ['Loan Repayment (EMI)', 'Capital Withdrawal / Drawings', 'Asset Purchase'];
const OTHER_CREDIT_LABELS = ['Loan Received', 'Capital Infusion', 'Interest Income', 'Sale of Asset'];

// The two Loan-side 'other' labels — picking either now requires a second
// step (which Loan Account this transaction belongs to, via
// LoanAccountSelector below), since LoanTransaction.loanAccountId is a
// required, non-nullable FK (see CLAUDE.md's Loans & Capital note).
// Deliberately per-row only, not wired into the bulk-assign toolbar — each
// row plausibly belongs to a different loan, unlike a bulk Expense/Party
// pick where the same category genuinely applies to many rows at once.
const LOAN_LABELS = ['Loan Received', 'Loan Repayment (EMI)'];

// A row's "side" (which half of the new taxonomy applies) is fixed by
// whether it's a debit or credit entry — an entry never has both.
function sideOf(e: BankStatementEntry): 'debit' | 'credit' {
  return e.debit != null ? 'debit' : 'credit';
}

// What type a row starts on: its own saved suggestion if it has one,
// otherwise the side's natural default (Expense for a debit, Party for a
// credit) — the common case for an unsuggested row.
function defaultLedgerType(e: BankStatementEntry): LedgerType {
  if (e.suggestedType) return e.suggestedType;
  return sideOf(e) === 'debit' ? 'expense' : 'party';
}

// The per-row Suggested Ledger cell — its own component (not inlined in the
// table .map()) so it can hold local "which type is the user currently
// looking at" state via a plain useState, the same way PartyLedgerPicker
// already manages its own search-box state. That local `type` selection is
// purely a display/in-progress choice — nothing is saved until the value
// control underneath it is actually picked/confirmed, mirroring the bulk
// toolbar's existing two-step bulkType/bulkValue pattern. Parent gives this
// a `key` derived from the entry's own saved fields (see call site) so a
// reload after a save elsewhere fully remounts it with fresh state, rather
// than leaving a stale in-progress type selection from before the reload.
function LedgerCell({ entry, parties, loanAccounts, onLoanAccountCreated, onAssign }: {
  entry: BankStatementEntry;
  parties: Party[];
  loanAccounts: LoanAccount[];
  onLoanAccountCreated: (account: LoanAccount) => void;
  onAssign: (label: string, type: LedgerType, partyId?: string, loanAccountId?: string) => void;
}) {
  const side = sideOf(entry);
  const [type, setType] = useState<LedgerType>(defaultLedgerType(entry));
  // Which 'other' label is currently selected — only meaningful while
  // choosing one of the two Loan labels, which need this held locally
  // until a LoanAccountSelector pick actually completes the save (unlike
  // every other 'other' label, which calls onAssign immediately on select).
  const [pendingOtherLabel, setPendingOtherLabel] = useState<string | null>(null);

  // Only treat the entry's own suggestedLabel/categoryConfirmed as "this is
  // what's saved for the currently-selected type" when the type selector is
  // still sitting on the entry's actual saved type — if the user has since
  // switched the selector to look at a different type, the value control
  // below starts blank/unconfirmed rather than carrying over a stale
  // suggestion that belongs to a different category entirely.
  const matchesSaved = type === entry.suggestedType;
  const currentLabel = matchesSaved ? entry.suggestedLabel : undefined;
  const saved = matchesSaved && entry.categoryConfirmed;

  const typeSelect = (
    <select
      value={type}
      onChange={ev => setType(ev.target.value as LedgerType)}
      style={{ fontSize: '0.72rem', padding: '0.15rem 0.3rem', width: '100%', boxSizing: 'border-box' }}
    >
      {side === 'debit' ? (
        <>
          <option value="expense">Expense</option>
          <option value="purchase">Purchase (Vendor)</option>
          <option value="transfer">Self Transfer</option>
          <option value="other">Other</option>
        </>
      ) : (
        <>
          <option value="party">Party (Customer)</option>
          <option value="transfer">Self Transfer</option>
          <option value="other">Other</option>
        </>
      )}
    </select>
  );

  let valueControl: React.ReactNode;
  if (type === 'expense') {
    valueControl = (
      <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
        <select
          value={currentLabel ?? ''}
          onChange={ev => { if (ev.target.value) onAssign(ev.target.value, type); }}
          style={{ fontSize: '0.78rem', padding: '0.25rem 0.4rem', width: '100%', boxSizing: 'border-box', borderColor: saved ? 'var(--success)' : undefined }}
          title={saved ? 'Saved — booked as a real Expense' : currentLabel ? 'Suggested — not yet saved' : 'No suggestion'}
        >
          <option value="">Uncategorized</option>
          <optgroup label="Direct Expenses">
            {EXPENSE_DIRECT_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
          </optgroup>
          <optgroup label="Indirect Expenses">
            {EXPENSE_INDIRECT_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
          </optgroup>
        </select>
        {currentLabel && !saved && (
          // A <select> doesn't fire onChange when you "pick" the option
          // that's already showing — so an already-correct suggestion could
          // never be confirmed without this: reselecting the same value
          // silently does nothing in a native dropdown. Confirms it as-is.
          <button
            type="button" className="btn btn-ghost btn-sm"
            style={{ padding: '0.2rem 0.4rem', fontSize: '0.72rem', flexShrink: 0 }}
            title="Confirm this suggestion — books it as a real Expense"
            onClick={() => onAssign(currentLabel, type)}
          >✓</button>
        )}
      </div>
    );
  } else if (type === 'party' || type === 'purchase') {
    // 'party' (credit) offers customer/both parties; 'purchase' (debit, a
    // vendor payment) offers vendor/both — both link to the exact same real
    // partyId FK, just filtered to the side-appropriate party list.
    const eligible = parties.filter(p => type === 'party' ? p.type !== 'vendor' : p.type !== 'customer');
    valueControl = (
      <PartyLedgerPicker
        currentLabel={currentLabel}
        saved={saved}
        parties={eligible}
        onSelect={p => onAssign(p.name, type, p.id)}
      />
    );
  } else if (type === 'transfer') {
    valueControl = saved ? (
      <span style={{ fontSize: '0.75rem', color: 'var(--success)', fontWeight: 600 }}>✓ Self Transfer</span>
    ) : (
      <button
        type="button" className="btn btn-ghost btn-sm" style={{ fontSize: '0.75rem', width: '100%' }}
        title="Marks this as an inter-account transfer — books nothing, excluded from every report"
        onClick={() => onAssign('Self Transfer', 'transfer')}
      >Mark as Self Transfer</button>
    );
  } else {
    // 'other' — never auto-suggested, so there's no "confirm an existing
    // suggestion" case to handle here, unlike 'expense' above; picking any
    // value is always a fresh manual choice. The two Loan labels are the
    // one exception: selecting one doesn't save immediately — it reveals a
    // LoanAccountSelector below, and the save only fires once an account is
    // actually picked/created there (see LOAN_LABELS' own comment above).
    const labels = side === 'debit' ? OTHER_DEBIT_LABELS : OTHER_CREDIT_LABELS;
    const selectedLabel = pendingOtherLabel ?? currentLabel ?? '';
    const isLoanLabel = LOAN_LABELS.includes(selectedLabel);
    valueControl = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
        <select
          value={selectedLabel}
          onChange={ev => {
            const v = ev.target.value;
            setPendingOtherLabel(v || null);
            if (v && !LOAN_LABELS.includes(v)) onAssign(v, type);
          }}
          style={{ fontSize: '0.78rem', padding: '0.25rem 0.4rem', width: '100%', boxSizing: 'border-box', borderColor: saved && !isLoanLabel ? 'var(--success)' : undefined }}
          title={saved ? 'Saved' : isLoanLabel ? 'Pick a Loan Account below to confirm' : 'Label-only — not booked to any report or ledger'}
        >
          <option value="">Select…</option>
          {labels.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        {isLoanLabel && (
          <LoanAccountSelector
            loanAccounts={loanAccounts}
            onCreated={onLoanAccountCreated}
            onSelect={account => { onAssign(selectedLabel, type, undefined, account.id); setPendingOtherLabel(null); }}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
      {typeSelect}
      {valueControl}
    </div>
  );
}

const EMPTY_FILTERS = {
  dateFrom: '', dateTo: '',
  description: '', reference: '',
  debitMin: '', debitMax: '',
  creditMin: '', creditMax: '',
  balanceMin: '', balanceMax: '',
};
function inNumRange(val: number | undefined, min: string, max: string): boolean {
  if (!min && !max) return true;
  if (val == null) return false;
  if (min && val < Number(min)) return false;
  if (max && val > Number(max)) return false;
  return true;
}

function EmptyRow({ cols, msg }: { cols: number; msg: string }) {
  return <tr><td colSpan={cols} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1.5rem', fontSize: '0.875rem' }}>{msg}</td></tr>;
}

const filterInputStyle: React.CSSProperties = { fontSize: '0.7rem', padding: '0.15rem 0.3rem', width: '100%', boxSizing: 'border-box' };

// The per-statement "Transactions" detail page (route /banking/:id) — see
// BankingPage.tsx (the list half) and TRACKER.md for the plan this shipped
// from. Adds a Suggested Ledger column (an Expense-category guess for
// debits, a Party-name guess for credits, computed once at upload time —
// see apps/api/src/bank-reconciliation/ledger-suggestion.ts) on top of the
// existing match-against-Payments workflow, plus bulk assignment and the
// filters/checkbox-select pattern already used on InvoicesPage/
// LorryReceiptsPage.
export default function BankTransactionsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [statement, setStatement] = useState<BankStatement | null>(null);
  const [entries, setEntries] = useState<BankStatementEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ text: string; level: 'success' | 'warning' } | null>(null);
  // Full Party records, not just names — PartyLedgerPicker needs a real
  // `id` to link a bank entry to (see BankStatementEntry.partyId), not just
  // a display string the old free-text/<datalist> input used.
  const [parties, setParties] = useState<Party[]>([]);
  // Same reasoning as parties above — LoanAccountSelector needs real
  // LoanAccount records to search/pick from.
  const [loanAccounts, setLoanAccounts] = useState<LoanAccount[]>([]);

  const [statusFilter, setStatusFilter] = useState<'' | 'matched' | 'unmatched'>('');
  const [ledgerFilter, setLedgerFilter] = useState<'' | 'blank' | 'unsaved' | 'saved'>('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkType, setBulkType] = useState<LedgerType>('expense');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkPartyId, setBulkPartyId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!id) return;
    load(id);
    partiesApi.list('', 1, 200).then(res => setParties(res.data)).catch(() => {});
    loanAccountsApi.list(1, 200).then(res => setLoanAccounts(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function load(statementId: string) {
    setLoading(true);
    try {
      const stmt = await bankReconApi.getStatement(statementId);
      setStatement(stmt);
      setEntries(stmt.entries ?? []);
    } finally {
      setLoading(false);
    }
  }

  // A full reload after every save rather than an optimistic local patch —
  // deliberate, now that 'expense' can be silently skipped server-side (an
  // already-matched entry, or a non-debit row caught in a mixed bulk
  // selection; see bank-recon.service.ts's bulkAssignLedger()). The server's
  // response only carries counts, not which specific ids were skipped, so an
  // optimistic patch could mark a skipped row as "saved" when it wasn't —
  // wrong for a real booked Expense in a way it never was for a loose label.
  // load() doesn't reset entries/statement to null, so this doesn't trigger
  // the full-page loading overlay — just a brief button-disable, same as
  // doAutoMatch's own reload-after-mutate pattern.
  async function assignLedger(entryIds: string[], label: string, type: LedgerType, partyId?: string, loanAccountId?: string) {
    if (!label.trim()) return;
    try {
      const res = await bankReconApi.bulkAssignLedger(entryIds, label.trim(), type, partyId, loanAccountId);
      if (id) await load(id);
      setMsg(res.skipped > 0
        ? { text: `${res.updated} saved, ${res.skipped} skipped (already matched to a payment, or doesn't fit this category).`, level: 'warning' }
        : null);
    } catch (err) {
      setMsg({ text: err instanceof Error ? err.message : 'Could not save ledger', level: 'warning' });
    }
  }

  async function doAutoMatch() {
    if (!id) return;
    setLoading(true); setMsg(null);
    try {
      const res = await bankReconApi.autoMatch(id);
      setMsg({ text: `Auto-matched ${res.matched} entries`, level: 'success' });
      await load(id);
    } finally {
      setLoading(false);
    }
  }

  async function doUnmatch(entryId: string) {
    setLoading(true); setMsg(null);
    try {
      await bankReconApi.unmatch(entryId);
      setEntries(prev => (prev ?? []).map(e => e.id === entryId ? { ...e, matchStatus: 'unmatched', matchedPaymentId: undefined } : e));
    } catch (err) {
      setMsg({ text: err instanceof Error ? err.message : 'Unmatch failed', level: 'warning' });
    } finally {
      setLoading(false);
    }
  }

  async function deleteStatement() {
    if (!statement) return;
    if (!window.confirm(`Delete this ${statement.bankName} statement (${entries?.length ?? 0} entries)? This cannot be undone.`)) return;
    setLoading(true); setMsg(null);
    try {
      await bankReconApi.deleteStatement(statement.id);
      navigate('/banking');
    } catch (err) {
      setMsg({ text: err instanceof Error ? err.message : 'Delete failed', level: 'warning' });
      setLoading(false);
    }
  }

  if (!statement || !entries) {
    return (
      <FeatureGate feature="bankReconciliation">
        <div className="page">
          <div className="page-header">
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/banking')}>← Back</button>
          </div>
          <div className="loading-overlay"><span className="spinner" /></div>
        </div>
      </FeatureGate>
    );
  }

  const matched = entries.filter(e => e.matchStatus === 'matched');
  const unmatched = entries.filter(e => e.matchStatus !== 'matched');
  const f = filters;
  const filteredEntries = entries.filter(e => {
    if (statusFilter === 'matched' && e.matchStatus !== 'matched') return false;
    if (statusFilter === 'unmatched' && e.matchStatus === 'matched') return false;
    if (ledgerFilter === 'blank' && e.suggestedLabel) return false;
    if (ledgerFilter === 'unsaved' && (!e.suggestedLabel || e.categoryConfirmed)) return false;
    if (ledgerFilter === 'saved' && !e.categoryConfirmed) return false;
    const d = e.date?.slice(0, 10) ?? '';
    if (f.dateFrom && d < f.dateFrom) return false;
    if (f.dateTo && d > f.dateTo) return false;
    if (f.description && !(e.description ?? '').toLowerCase().includes(f.description.toLowerCase())) return false;
    if (f.reference && !(e.reference ?? '').toLowerCase().includes(f.reference.toLowerCase())) return false;
    if (!inNumRange(e.debit != null ? Number(e.debit) : undefined, f.debitMin, f.debitMax)) return false;
    if (!inNumRange(e.credit != null ? Number(e.credit) : undefined, f.creditMin, f.creditMax)) return false;
    if (!inNumRange(e.balance != null ? Number(e.balance) : undefined, f.balanceMin, f.balanceMax)) return false;
    return true;
  });
  const filtersActive = statusFilter !== '' || ledgerFilter !== '' || Object.values(f).some(v => v !== '');
  const totalDebit = filteredEntries.reduce((s, e) => s + (e.debit != null ? Number(e.debit) : 0), 0);
  const totalCredit = filteredEntries.reduce((s, e) => s + (e.credit != null ? Number(e.credit) : 0), 0);

  return (
    <FeatureGate feature="bankReconciliation">
      <div className="page">
        <div className="page-header">
          <div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/banking')} style={{ marginBottom: '0.4rem' }}>← Statements</button>
            <h1 className="page-title">{statement.bankName} — Transactions</h1>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {msg && <span style={{ fontSize: '0.78rem', color: msg.level === 'warning' ? 'var(--warning)' : 'var(--success)' }}>{msg.text}</span>}
            <button className="btn btn-ghost btn-sm" disabled={loading} onClick={doAutoMatch}>Auto-Match</button>
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} disabled={loading} onClick={deleteStatement}>Delete Statement</button>
          </div>
        </div>

        <div className="card" style={{ padding: '1rem 1.25rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.8rem' }}>
            <button
              onClick={() => setStatusFilter(v => v === 'matched' ? '' : 'matched')}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--success)', fontWeight: 700, textDecoration: statusFilter === 'matched' ? 'underline' : 'none' }}
            >✓ Matched: {matched.length}</button>
            <button
              onClick={() => setStatusFilter(v => v === 'unmatched' ? '' : 'unmatched')}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--warning)', fontWeight: 700, textDecoration: statusFilter === 'unmatched' ? 'underline' : 'none' }}
            >? Unmatched: {unmatched.length}</button>
            <span style={{ color: 'var(--text-muted)' }}>Total: {entries.length}</span>
            {filtersActive && (
              <button
                onClick={() => { setFilters(EMPTY_FILTERS); setStatusFilter(''); setLedgerFilter(''); }}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-muted)', textDecoration: 'underline' }}
              >Clear filters</button>
            )}
          </div>
        </div>

        {selected.size > 0 && (
          <div className="card" style={{ padding: '0.85rem 1.25rem', marginBottom: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: '0.85rem' }}>Bulk Operations — {selected.size} selected</strong>
            <select
              value={bulkType}
              onChange={e => { setBulkType(e.target.value as LedgerType); setBulkValue(e.target.value === 'transfer' ? 'Self Transfer' : ''); setBulkPartyId(undefined); }}
              style={{ width: 'auto', fontSize: '0.82rem' }}
            >
              <option value="expense">Expense Category</option>
              <option value="purchase">Purchase (Vendor)</option>
              <option value="party">Party Ledger (Customer)</option>
              <option value="transfer">Self Transfer</option>
              <option value="other">Other (Loan / Capital / Asset)</option>
            </select>
            {bulkType === 'expense' ? (
              <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{ maxWidth: 260, fontSize: '0.82rem' }}>
                <option value="">Select a category…</option>
                <optgroup label="Direct Expenses">
                  {EXPENSE_DIRECT_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
                </optgroup>
                <optgroup label="Indirect Expenses">
                  {EXPENSE_INDIRECT_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
                </optgroup>
              </select>
            ) : bulkType === 'party' || bulkType === 'purchase' ? (
              <div style={{ maxWidth: 260, width: '100%' }}>
                <PartyLedgerPicker
                  currentLabel={bulkValue}
                  saved={false}
                  parties={parties.filter(p => bulkType === 'party' ? p.type !== 'vendor' : p.type !== 'customer')}
                  onSelect={p => { setBulkValue(p.name); setBulkPartyId(p.id); }}
                />
              </div>
            ) : bulkType === 'transfer' ? (
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>No category needed — books nothing, excluded from every report.</span>
            ) : (
              // 'other' — a bulk selection can span both debit and credit
              // rows, so both sides' labels are offered; bulkAssignLedger()
              // already validates each entry's own label-side match and
              // skips any that don't (surfaced via the skipped-count
              // warning), so a debit row picked up in a "Loan Received"
              // (credit-only) bulk-apply is safely skipped, not miscategorized.
              <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{ maxWidth: 260, fontSize: '0.82rem' }}>
                <option value="">Select a category…</option>
                <optgroup label="Debit-side">
                  {OTHER_DEBIT_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
                </optgroup>
                <optgroup label="Credit-side">
                  {OTHER_CREDIT_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
                </optgroup>
              </select>
            )}
            <button
              className="btn btn-primary btn-sm"
              disabled={!bulkValue.trim() || ((bulkType === 'party' || bulkType === 'purchase') && !bulkPartyId)}
              onClick={async () => {
                await assignLedger(Array.from(selected), bulkValue, bulkType, bulkPartyId);
                setSelected(new Set()); setBulkValue(''); setBulkPartyId(undefined);
              }}
            >Apply to {selected.size}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>Clear selection</button>
          </div>
        )}

        <div className="card">
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox" style={{ width: 'auto', cursor: 'pointer' }}
                      checked={filteredEntries.length > 0 && filteredEntries.every(e => selected.has(e.id))}
                      onChange={e => setSelected(e.target.checked ? new Set(filteredEntries.map(en => en.id)) : new Set())}
                    />
                  </th>
                  <th>Date</th><th>Description</th><th>Reference</th>
                  <th style={{ textAlign: 'right' }}>Debit</th><th style={{ textAlign: 'right' }}>Credit</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                  <th>Suggested Ledger</th>
                  <th>Status</th><th>Action</th>
                </tr>
                <tr>
                  <th />
                  <th style={{ padding: '0.25rem 0.4rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                      <input type="date" value={f.dateFrom} onChange={e => setFilters(p => ({ ...p, dateFrom: e.target.value }))} style={filterInputStyle} title="From date" />
                      <input type="date" value={f.dateTo} onChange={e => setFilters(p => ({ ...p, dateTo: e.target.value }))} style={filterInputStyle} title="To date" />
                    </div>
                  </th>
                  <th style={{ padding: '0.25rem 0.4rem' }}>
                    <input value={f.description} onChange={e => setFilters(p => ({ ...p, description: e.target.value }))} placeholder="Filter…" style={filterInputStyle} />
                  </th>
                  <th style={{ padding: '0.25rem 0.4rem' }}>
                    <input value={f.reference} onChange={e => setFilters(p => ({ ...p, reference: e.target.value }))} placeholder="Filter…" style={filterInputStyle} />
                  </th>
                  <th style={{ padding: '0.25rem 0.4rem' }}>
                    <div style={{ display: 'flex', gap: '0.15rem' }}>
                      <input type="number" value={f.debitMin} onChange={e => setFilters(p => ({ ...p, debitMin: e.target.value }))} placeholder="Min" style={filterInputStyle} />
                      <input type="number" value={f.debitMax} onChange={e => setFilters(p => ({ ...p, debitMax: e.target.value }))} placeholder="Max" style={filterInputStyle} />
                    </div>
                  </th>
                  <th style={{ padding: '0.25rem 0.4rem' }}>
                    <div style={{ display: 'flex', gap: '0.15rem' }}>
                      <input type="number" value={f.creditMin} onChange={e => setFilters(p => ({ ...p, creditMin: e.target.value }))} placeholder="Min" style={filterInputStyle} />
                      <input type="number" value={f.creditMax} onChange={e => setFilters(p => ({ ...p, creditMax: e.target.value }))} placeholder="Max" style={filterInputStyle} />
                    </div>
                  </th>
                  <th style={{ padding: '0.25rem 0.4rem' }}>
                    <div style={{ display: 'flex', gap: '0.15rem' }}>
                      <input type="number" value={f.balanceMin} onChange={e => setFilters(p => ({ ...p, balanceMin: e.target.value }))} placeholder="Min" style={filterInputStyle} />
                      <input type="number" value={f.balanceMax} onChange={e => setFilters(p => ({ ...p, balanceMax: e.target.value }))} placeholder="Max" style={filterInputStyle} />
                    </div>
                  </th>
                  <th style={{ padding: '0.25rem 0.4rem' }}>
                    <select value={ledgerFilter} onChange={e => setLedgerFilter(e.target.value as typeof ledgerFilter)} style={{ ...filterInputStyle, width: '100%' }}>
                      <option value="">All</option>
                      <option value="blank">Blank Records</option>
                      <option value="unsaved">Unsaved Records</option>
                      <option value="saved">Saved Records</option>
                    </select>
                  </th>
                  <th style={{ padding: '0.25rem 0.4rem' }}>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as typeof statusFilter)} style={{ ...filterInputStyle, width: '100%' }}>
                      <option value="">All</option>
                      <option value="matched">Matched</option>
                      <option value="unmatched">Unmatched</option>
                    </select>
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredEntries.length === 0 ? (
                  <EmptyRow cols={10} msg={entries.length === 0 ? 'No entries in this statement' : 'No entries match your filters'} />
                ) : filteredEntries.map(e => {
                  return (
                    <tr key={e.id} style={{ background: e.matchStatus === 'matched' ? 'var(--row-tint-yellow, #f0fdf4)' : undefined }}>
                      <td>
                        <input
                          type="checkbox" style={{ width: 'auto', cursor: 'pointer' }}
                          checked={selected.has(e.id)}
                          onChange={ev => setSelected(prev => { const s = new Set(prev); ev.target.checked ? s.add(e.id) : s.delete(e.id); return s; })}
                        />
                      </td>
                      <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{e.date?.slice(0, 10)}</td>
                      <td style={{ fontSize: '0.8rem', minWidth: 220, maxWidth: 340, whiteSpace: 'normal', wordBreak: 'break-word' }}>{e.description}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-muted)' }}>{e.reference ?? '—'}</td>
                      <td style={{ textAlign: 'right', color: 'var(--danger)', fontSize: '0.8rem' }}>{e.debit != null ? fmtN(Number(e.debit)) : '—'}</td>
                      <td style={{ textAlign: 'right', color: 'var(--success)', fontWeight: 600, fontSize: '0.8rem' }}>{e.credit != null ? fmtN(Number(e.credit)) : '—'}</td>
                      <td style={{ textAlign: 'right', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{e.balance != null ? fmtN(Number(e.balance)) : '—'}</td>
                      <td style={{ minWidth: 200 }}>
                        <LedgerCell
                          // Forces a full remount (fresh local `type` state)
                          // whenever the entry's own saved fields change
                          // elsewhere — a bulk-apply or a reload after this
                          // row's own save — same key-based pattern already
                          // established for the old cell, now covering the
                          // type selector too.
                          key={`${e.id}:${e.suggestedType ?? ''}:${e.suggestedLabel ?? ''}:${e.categoryConfirmed}`}
                          entry={e}
                          parties={parties}
                          loanAccounts={loanAccounts}
                          onLoanAccountCreated={account => setLoanAccounts(prev => [...prev, account])}
                          onAssign={(label, type, partyId, loanAccountId) => assignLedger([e.id], label, type, partyId, loanAccountId)}
                        />
                      </td>
                      <td>
                        {e.matchStatus === 'matched'
                          ? <span style={{ color: 'var(--success)', fontSize: '0.75rem', fontWeight: 700 }}>✓ Matched</span>
                          : <span style={{ color: 'var(--warning)', fontSize: '0.75rem' }}>Unmatched</span>}
                      </td>
                      <td>
                        {e.matchStatus === 'matched' && (
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.72rem' }} disabled={loading} onClick={() => doUnmatch(e.id)}>Unmatch</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {filteredEntries.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                    <td colSpan={4} style={{ textAlign: 'right', fontSize: '0.8rem', padding: '0.4rem' }}>
                      {filtersActive ? `Total (${filteredEntries.length} filtered):` : 'Total:'}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--danger)', fontSize: '0.8rem', padding: '0.4rem' }}>{fmtN(totalDebit)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--success)', fontSize: '0.8rem', padding: '0.4rem' }}>{fmtN(totalCredit)}</td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </FeatureGate>
  );
}
